import { useCallback, useState } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  HydratedTable,
  useTableConnectedAssets,
  useTableSharesSearch,
  useChangeTableOwner,
  Member,
} from "@zapier/client";
import { ChangeOwnerModal } from "@zapier/shared";
import {
  AssetTypes,
  extractCustomuserId,
  useNotification,
} from "@zapier/utils";
import { useEmitTableChangeOwnerModalEvent } from "@zapier/shared/src/events";

interface Props {
  table?: HydratedTable;
  isOpen: boolean;
  onCloseModal: () => void;
  preselectedUser?: Member;
  folderId?: string; // used for cache invalidation
}

const TableChangeOwnerContainer = ({
  table,
  isOpen,
  onCloseModal,
  preselectedUser,
  folderId,
}: Props) => {
  const t = useTranslations("table-change-owner-modal");
  const { createNotification } = useNotification();
  const {
    transformedAssets,
    isLoading: isLoadingAssets,
    isFetching: isFetchingAssets,
  } = useTableConnectedAssets(isOpen ? table?.id : undefined);
  const { emit } = useEmitTableChangeOwnerModalEvent();

  const [userSearch, setUserSearch] = useState("");

  const { users, isLoading: isUsersLoading } = useTableSharesSearch({
    tableId: isOpen ? table?.id || "" : "",
    ownerId: table?.owner?.id,
    search: userSearch,
    type: "customuser",
  });

  const { changeTableOwner, isSuccess, isError } = useChangeTableOwner({
    folderId,
  });

  const onSearch = useCallback((search: string) => {
    setUserSearch(search);
  }, []);

  if (!table || !isOpen) {
    return;
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setUserSearch("");
      emit({ optionSelected: "cancel", tableId: table.id });
      onCloseModal();
    }
  };
  const onSubmit = async (ownerId: string) => {
    const customuserId = preselectedUser?.id || extractCustomuserId(ownerId);
    if (customuserId) {
      emit({ optionSelected: "confirm", tableId: table.id });
      await changeTableOwner(
        {
          tableId: table.id,
          ownerId: customuserId,
        },
        {
          onSuccess: () => {
            createNotification({
              message: t("notifications.change-owner-success"),
              type: "success",
            });
          },

          onError: (error) => {
            createNotification({
              message: t("notifications.change-owner-error", {
                errorMessage: error.message,
              }),
              type: "error",
            });
          },
        },
      );

      if (isSuccess) {
        emit({
          eventAction: "view",
          optionSelected: "success",
          tableId: table.id,
        });
      }

      if (isError) {
        emit({
          eventAction: "view",
          optionSelected: "error",
          tableId: table.id,
        });
      }
    }
  };

  return (
    <ChangeOwnerModal
      users={users}
      id={table.id}
      title={table.name}
      currentOwnerId={table.owner?.id}
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      linkedAssets={transformedAssets}
      assetType={AssetTypes.Table}
      isLoading={isLoadingAssets || isFetchingAssets}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      onSubmit={onSubmit}
      preselectedUser={preselectedUser}
    />
  );
};

export default TableChangeOwnerContainer;
